/**
 * One place that decides what of a URL may reach an analytics or monitoring
 * vendor (GA, PostHog, Vercel Speed Insights, Sentry).
 *
 * Two kinds of secret ride in this app's URLs:
 *  - PATH segments that are a credential: the email claim token
 *    (/email/claim/<token>) and a shared chat's token (/chat/x/share/<token>).
 *    These are replaced by a route template, so the page is still counted.
 *  - QUERY and FRAGMENT parameters: sign-in tokens and session JWTs, emails,
 *    link and organization codes. These are removed.
 *
 * Dependency-free and pure, so it runs inside every vendor's before-send hook
 * and is unit tested on its own (analyticsUrl.test.mjs).
 */

/** Path prefixes whose next segment is a secret, and the template that replaces them. */
const SECRET_PATH_SEGMENTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/email\/claim\/[^/?#]+/, '/email/claim/:token'],
  [/^\/chat\/x\/share\/[^/?#]+/, '/chat/x/share/:token'],
]

/** Query/fragment keys that must never reach a vendor. `c` is the org join code. */
export const SENSITIVE_URL_PARAMS: readonly string[] = ['token', 'email', 'authorization', 'code', 'session', 'c']

/** `/email/claim/abc123` → `/email/claim/:token`. Anything else comes back unchanged. */
export function scrubSecretPath(pathname: string): string {
  for (const [pattern, template] of SECRET_PATH_SEGMENTS) {
    if (pattern.test(pathname)) return pathname.replace(pattern, template)
  }
  return pathname
}

function scrubParams(params: URLSearchParams): boolean {
  let touched = false
  for (const key of SENSITIVE_URL_PARAMS) {
    if (params.has(key)) {
      params.delete(key)
      touched = true
    }
  }
  return touched
}

/**
 * Scrub a full URL or a path (`/x?y#z`): secret path segments templated,
 * sensitive query and fragment parameters removed. A string that is neither a
 * URL nor a path, or that does not parse, comes back untouched, so this is
 * safe to run over every string property of an event.
 */
export function scrubAnalyticsUrl(value: string): string {
  if (!value) return value
  const absolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(value)
  if (!absolute && !/^[/?#]/.test(value)) return value
  try {
    const url = new URL(value, absolute ? undefined : 'https://app.itsbaba.com')
    let touched = false
    const path = scrubSecretPath(url.pathname)
    if (path !== url.pathname) {
      url.pathname = path
      touched = true
    }
    if (scrubParams(url.searchParams)) touched = true
    if (url.hash.length > 1) {
      const hashParams = new URLSearchParams(url.hash.slice(1))
      if (scrubParams(hashParams)) {
        touched = true
        const rest = hashParams.toString()
        url.hash = rest ? `#${rest}` : ''
      }
    }
    if (!touched) return value
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
  } catch {
    return value
  }
}
